// ============================================================================
// OFFLINE PIN (Q-018)
//
// Server PIN hash'ini qurilmaga bermaydi. Xodim shu kassada online kirganda
// kassa PIN'dan sekin kalit (PBKDF2, 150 000 aylanish) hosil qilib saqlaydi.
// Internet yo'q paytda kiritilgan PIN shu kalitlar bilan solishtiriladi.
// PIN almashsa yoki xodim o'chirilsa — katalog yangilanganda kalit o'chadi.
// ============================================================================

import { meta } from './idb';
import type { BootStaff } from './logic';

interface Verifier {
  hash: string;
  pin_set_at: string;
}

type Verifiers = Record<string, Verifier>;

const ITERATIONS = 150_000;

function hex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function deviceSalt(): Promise<string> {
  const existing = await meta.get<string>('pin_salt');
  if (existing) return existing;
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const salt = hex(bytes.buffer);
  await meta.set('pin_salt', salt);
  return salt;
}

async function derive(pin: string, terminalId: string, scope = ''): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits']);
  const salt = enc.encode(`${await deviceSalt()}:${terminalId}${scope}`);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations: ITERATIONS }, key, 256);
  return hex(bits);
}

/** Online kirish muvaffaqiyatli bo'lgach — shu xodim uchun offline kalit */
export async function rememberPin(terminalId: string, staff: Pick<BootStaff, 'id' | 'pin_set_at'>, pin: string): Promise<void> {
  try {
    const all = (await meta.get<Verifiers>('pins')) ?? {};
    all[staff.id] = { hash: await derive(pin, terminalId), pin_set_at: staff.pin_set_at };
    await meta.set('pins', all);
  } catch {
    /* saqlab bo'lmadi — offline kirish shu xodim uchun ishlamaydi, online ishlaydi */
  }
}

/** Offline: PIN kimniki (faqat shu kassada avval online kirganlar) */
export async function checkPinOffline(terminalId: string, pin: string): Promise<string | null> {
  const all = (await meta.get<Verifiers>('pins')) ?? {};
  if (Object.keys(all).length === 0) return null;
  const hash = await derive(pin, terminalId);
  for (const [staffId, v] of Object.entries(all)) {
    if (v.hash === hash) return staffId;
  }
  return null;
}

/** Xodim o'chirilgan yoki PIN/parol almashgan bo'lsa — eski kalitni o'chirish */
export async function prunePins(staff: readonly Pick<BootStaff, 'id' | 'pin_set_at' | 'password_set_at'>[]): Promise<void> {
  const all = (await meta.get<Verifiers>('pins')) ?? {};
  const current = new Map(staff.map((s) => [s.id, s.pin_set_at]));
  let changed = false;
  for (const [id, v] of Object.entries(all)) {
    const setAt = current.get(id);
    if (setAt === undefined || new Date(setAt).getTime() !== new Date(v.pin_set_at).getTime()) {
      delete all[id];
      changed = true;
    }
  }
  if (changed) await meta.set('pins', all);

  const pws = (await meta.get<Verifiers>('pws')) ?? {};
  const pwAt = new Map(staff.map((s) => [s.id, s.password_set_at ?? null]));
  let pwChanged = false;
  for (const [id, v] of Object.entries(pws)) {
    const setAt = pwAt.get(id);
    if (!setAt || new Date(setAt).getTime() !== new Date(v.pin_set_at).getTime()) {
      delete pws[id];
      pwChanged = true;
    }
  }
  if (pwChanged) await meta.set('pws', pws);
}

// ---------------------------------------------------------------------------
// OFFLINE 2-PAROL (Q-021): xuddi PIN kabi — online to'g'ri kiritilgach sekin kalit saqlanadi.
// Internet yo'q paytda 3 marta xato -> shu qurilmada 60 daqiqa blok.
// ---------------------------------------------------------------------------
export async function rememberPassword(terminalId: string, staffId: string, setAt: string | null | undefined, password: string): Promise<void> {
  if (!setAt) return;
  try {
    const all = (await meta.get<Verifiers>('pws')) ?? {};
    all[staffId] = { hash: await derive(password, terminalId, `:pw:${staffId}`), pin_set_at: setAt };
    await meta.set('pws', all);
  } catch {
    /* saqlanmadi — offline kirishda parol tekshirib bo'lmaydi */
  }
}

export async function hasPasswordOffline(staffId: string): Promise<boolean> {
  const all = (await meta.get<Verifiers>('pws')) ?? {};
  return Boolean(all[staffId]);
}

type OfflineLocks = Record<string, { fails: number; until: number | null }>;

export async function checkPasswordOffline(
  terminalId: string,
  staffId: string,
  password: string,
): Promise<{ ok: true } | { ok: false; locked: boolean; left: number; until: number | null }> {
  const locks = (await meta.get<OfflineLocks>('pw_locks')) ?? {};
  const cur = locks[staffId] ?? { fails: 0, until: null };
  if (cur.until && cur.until > Date.now()) return { ok: false, locked: true, left: 0, until: cur.until };
  const all = (await meta.get<Verifiers>('pws')) ?? {};
  const v = all[staffId];
  if (v && v.hash === (await derive(password, terminalId, `:pw:${staffId}`))) {
    delete locks[staffId];
    await meta.set('pw_locks', locks);
    return { ok: true };
  }
  const fails = (cur.until && cur.until <= Date.now() ? 0 : cur.fails) + 1;
  const until = fails >= 3 ? Date.now() + 60 * 60_000 : null;
  locks[staffId] = { fails: until ? 0 : fails, until };
  await meta.set('pw_locks', locks);
  return { ok: false, locked: Boolean(until), left: Math.max(0, 3 - fails), until };
}

/** Bu kassada offline kira oladigan xodimlar */
export async function offlineStaffIds(): Promise<string[]> {
  return Object.keys((await meta.get<Verifiers>('pins')) ?? {});
}
